import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { clearSelectedAccount } from '../../../actions/accountActions';
import { loadSelectedCompany } from '../../../actions/companyActions';
import * as companyApi from '../../../api/companyApi';
import * as userSettingsApi from '../../../api/userSettingsApi';
import { applyThemeSettings, clearThemeSettings } from '../../../services/portalBranding';
import { Loadable } from '../../../models';
import { Company } from '../../../models/company';
import { QueryResult } from '../../../models/queryResult';
import { AppState } from '../../../models/state/appState';
import { getDefaultFilterCompany } from '../../../models/state/filterSettingsState';
import SearchError from '../../search/SearchError';
import SearchInfo from '../../search/SearchInfo';
import SearchWarning from '../../search/SearchWarning';
import SelectorMenuLoading from '../../ui/SelectorMenuLoading';

interface Props {
  selectedCompanyId?: number;
}

type SearchStatus = 'Empty' | 'NoResults' | 'Results' | 'Error' | 'Loading';

export default function CompanyMenu({ selectedCompanyId }: Props) {
  const dispatch: Dispatch<any> = useDispatch();
  const company = useSelector<AppState, Loadable<Company>>((state) => state.filter.company);
  const userId = useSelector<AppState, string>((state) => state.userSettings.userId);
  const currentCompany: Loadable<Company> =
    selectedCompanyId === company.id ? company : getDefaultFilterCompany();

  const [searchText, setSearchText] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [count, setCount] = useState<number>();
  const [totalCount, setTotalCount] = useState<number>();
  const [status, setStatus] = useState<SearchStatus>('Empty');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  function search() {
    setStatus('Loading');
    companyApi
      .get({
        take: 30,
        searchTerm: searchText,
        wrapWith: {
          totalCount: true,
        },
      })
      .then((queryResult: QueryResult<Company>) => {
        setCompanies(queryResult.result);
        setStatus(queryResult.result.length ? 'Results' : 'NoResults');
        setCount(queryResult.count);
        setTotalCount(queryResult.totalCount);
      })
      .catch(() => setStatus('Error'));
  }

  function onSelectCompany(selectedCompany: Company) {
    setIsMenuOpen(false);
    dispatch(clearSelectedAccount());
    if (selectedCompany.id !== selectedCompanyId) {
      userSettingsApi
        .getTheme(userId, selectedCompany.id, null)
        .then((reply: Record<string, string> | null) => {
          if (reply !== null) {
            applyThemeSettings(reply);
            return;
          }

          clearThemeSettings();
        })
        .catch(() => {
          clearThemeSettings();
        });

      dispatch(loadSelectedCompany(selectedCompany.id));
    }
  }

  function renderTitleAlt(): string {
    if (currentCompany.isLoading) {
      return 'Loading...';
    }

    if (currentCompany.error || currentCompany.id === 0) {
      return 'Company';
    }

    const companySpan = currentCompany.name + ' (' + currentCompany.id + ')';
    return currentCompany.companyStatus && currentCompany.companyStatus.limitedAccess
      ? companySpan + '- Limited access'
      : companySpan;
  }

  function renderTitle() {
    if (currentCompany.isLoading) {
      return 'Loading...';
    }

    if (currentCompany.error || currentCompany.id === 0) {
      return 'Company';
    }

    const companySpan = (
      <>
        {currentCompany.name} ({currentCompany.id})
      </>
    );

    if (currentCompany.companyStatus && currentCompany.companyStatus.limitedAccess) {
      return (
        <>
          {companySpan} <span className="fa fa-lock" title="limited-access"></span>
        </>
      );
    }

    return companySpan;
  }

  function renderMenuItems(): React.JSX.Element {
    const items = companies.map((c) => (
      <li className={currentCompany.id === c.id ? 'selected' : ''} key={c.id}>
        <Link className="menuItemLink" to={`/company/${c.id}/sim`} onClick={() => onSelectCompany(c)}>
          {c.name}
        </Link>
      </li>
    ));

    if (items.length === 0) {
      return <SearchInfo content="No results." />;
    }

    return (
      <div className="search-results noindent">
        <ul>{items}</ul>
        {totalCount === count ? (
          <SearchInfo
            content={
              <span>
                Search found <strong>{totalCount}</strong>
                &nbsp;{totalCount === 1 ? 'result' : 'results'}.
              </span>
            }
          />
        ) : (
          <SearchWarning count={count!} totalCount={totalCount!} />
        )}
      </div>
    );
  }

  function renderResultsSection(): React.JSX.Element | null {
    switch (status) {
      case 'Loading':
        return <SelectorMenuLoading />;
      case 'Results':
        return renderMenuItems();
      case 'NoResults':
        return <SearchInfo content="No results." />;
      case 'Empty':
        return null;
      case 'Error':
        return <SearchError content="An error occurred when attempting to search for the company." />;
      default:
        return <div>Unknown</div>;
    }
  }

  return (
    <ul className="nav navbar-nav selector-menu selected-company">
      <li className={`dropdown selector-title${isMenuOpen ? ' open show' : ''}`} ref={menuRef}>
        <a
          className="dropdown-toggle"
          id="company-selector-menu"
          href="#"
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            // Deliberately not stopping propagation: the click has to reach document so the account
            // menu's outside-click listener closes it, otherwise both dropdowns render on top of
            // each other. This menu's own listener ignores clicks inside menuRef.
            setIsMenuOpen((prev) => !prev);
          }}
        >
          <span id="companyTitle" title={renderTitleAlt()}>
            {renderTitle()}
          </span>
        </a>
        <ul
          className={`dropdown-menu csm-dropdown-search${isMenuOpen ? ' show' : ''}`}
          aria-labelledby="company-selector-menu"
        >
          <div className="input-group search-section">
            <input
              type="text"
              className="form-control search-box"
              name="companySearch"
              onChange={(e) => setSearchText(e.target.value)}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.keyCode === 13) {
                  search();
                }
              }}
              placeholder="Search..."
            />
            <Button
              variant="primary"
              aria-label="Submit search"
              type="button"
              onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                search();
              }}
            >
              <i className="fa-solid fa-magnifying-glass"></i>
            </Button>
          </div>
          {renderResultsSection()}
        </ul>
      </li>
    </ul>
  );
}
